import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useChangePassword } from '@nhost/react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import clsx from 'clsx'

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const { changePassword } = useChangePassword()

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [message, setMessage] = useState<string | null>(null)
  const [showRules, setShowRules] = useState(false)

  const passwordRules = [
    {
      label: 'Au moins 8 caractères',
      valid: newPassword.length >= 8,
    },
    {
      label: 'Une lettre majuscule',
      valid: /[A-Z]/.test(newPassword),
    },
    {
      label: 'Une lettre minuscule',
      valid: /[a-z]/.test(newPassword),
    },
    {
      label: 'Un chiffre',
      valid: /[0-9]/.test(newPassword),
    },
    {
      label: 'Un caractère spécial',
      valid: /[^A-Za-z0-9]/.test(newPassword),
    },
  ]

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (newPassword !== confirmPassword) {
      setMessage('Les mots de passe ne correspondent pas.')
      return
    }

    const allValid = passwordRules.every((rule) => rule.valid)
    if (!allValid) {
      setMessage('Le mot de passe ne respecte pas les critères de sécurité.')
      return
    }

    setStatus('loading')

    try {
      const { error } = await changePassword(newPassword)
      if (error) throw error

      setStatus('success')
      setMessage('Mot de passe modifié avec succès. Redirection en cours...')
      setTimeout(() => navigate('/downloadPage'), 2000)
    } catch (err: any) {
      setStatus('error')
      setMessage(err.message || 'Une erreur est survenue.')
    }
  }

  return (
    <div className="flex justify-center mt-10 px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Réinitialiser le mot de passe</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <Input
              type="password"
              placeholder="Nouveau mot de passe"
              value={newPassword}
              onFocus={() => setShowRules(true)}
              onBlur={() => {
                if (!newPassword) setShowRules(false)
              }}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />

            {showRules && (
              <div className="text-sm bg-gray-50 p-3 rounded border border-gray-200 space-y-1">
                {passwordRules.map((rule, idx) => (
                  <div
                    key={idx}
                    className={clsx('transition-all', {
                      'text-green-600': rule.valid,
                      'text-red-600': !rule.valid,
                    })}
                  >
                    • {rule.label}
                  </div>
                ))}
              </div>
            )}

            <Input
              type="password"
              placeholder="Confirmer le mot de passe"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />

            <Button
              type="submit"
              disabled={status === 'loading'}
              className="w-full"
            >
              {status === 'loading' ? 'Chargement…' : 'Changer le mot de passe'}
            </Button>

            {message && (
              <p
                className={clsx('text-sm mt-2 text-center', {
                  'text-green-600': status === 'success',
                  'text-red-600': status === 'error',
                })}
              >
                {message}
              </p>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
